/**
 * Default number of items to show per page
 */
export const DEFAULT_PAGE_SIZE = 50;

/**
 * Default starting page number (1-based)
 */
export const DEFAULT_PAGE_NUMBER = 1;

/**
 * Available options for items per page in UI dropdowns
 */
export const PAGINATION_OPTIONS = [10, 25, 50];

/**
 * Delay in milliseconds before triggering a search after user input
 */
export const SEARCH_DEBOUNCE_TIMEOUT = 300;

/**
 * Sort order type.
 * Used to specify the direction of sorting in API requests.
 */
export type SortOrder = 'ASC' | 'DESC';

/**
 * Pagination parameters.
 * Used to control the "windowing" of paginated data.
 */
export interface PaginationParams {
  /** Page number to fetch (1-based) */
  page?: number;
  /** Number of items to fetch per page */
  take?: number;
}

/**
 * Data filtering and sorting parameters.
 * Used to filter and sort the data set before pagination.
 */
export interface FilterSortParams {
  /** Optional search term to filter results */
  search?: string;
  /** Field to sort results by */
  sortField?: string;
  /** Sort direction ('asc' or 'desc') */
  sortOrder?: SortOrder;
  sortOptions?: {
    field: string;
    order: SortOrder;
  }[];
}

/**
 * Pagination metadata structure.
 * Contains information about the current page, total items, and navigation state.
 */
export interface PageMetaDto {
  /** Current page number (1-based) */
  page: number;
  /** Number of items per page */
  take: number;
  /** Total number of items across all pages */
  itemCount: number;
  /** Total number of pages */
  pageCount: number;
  /** Whether there is a previous page available */
  hasPreviousPage: boolean;
  /** Whether there is a next page available */
  hasNextPage: boolean;
}

/**
 * Generic paginated response structure.
 * Used as a wrapper for any paginated API response.
 * @template T - The type of items being paginated
 * 
 * @example
 * ```typescript
 * interface User { id: number; name: string; }
 * 
 * // API response type for paginated users
 * type PaginatedUsers = PaginatedResponse<User>;
 * 
 * // Example response
 * const response: PaginatedUsers = {
 *   data: [{ id: 1, name: 'John' }],
 *   meta: {
 *     page: 1,
 *     take: 10,
 *     itemCount: 100,
 *     pageCount: 10,
 *     hasPreviousPage: false,
 *     hasNextPage: true
 *   }
 * };
 * ```
 */
export interface PaginatedResponse<T> {
  /** Array of paginated items */
  data: T[];
  /** Pagination metadata */
  meta: PageMetaDto;
}
